// client/src/components/pages/LexiStar/Dashboard/TripsDrill.tsx
//
// The trips behind a figure (ST-00188 M4-D; LS-03 FR-RPT-006 "every aggregate
// shall drill through to the underlying trips"). m4-contracts §4 has every
// KPI value carry `drill: {route: '/lexistar/trips?…'}`; this is that page.
// Route /lexistar/trips (the list — /lexistar/trips/:id stays trip detail),
// page permission lexistar.trips.view.
//
// Query (the metric pack's tripsDrill): from, to (SAST days), one scope
// filter — lane_id, route_id, asset, driver_id, site_id, customer, material,
// run_id — plus stage (comma list) and flag (late | variance_breach |
// above_route_norm). GET /lexistar/trips applies every one of them
// server-side, so the list, its count and the page limit are the server's. The
// lane is only looked up to name it on its chip.
//
// ST-00207 (m4b-contracts §5, phone pass): under 640 px or on a touch screen the table is a
// list of cards — reference and stage, planned start and truck, from → to, net mass, km and fuel
// — each opening its trip, with no sideways scrolling. The desktop table is unchanged.
//
// ST-00318: also the sidebar's "Trip list" (LexiStar · Operate). Opened without a drill filter
// it is "Trip list": every trip planned to start in the period (today by default); with one it
// stays "Trips behind the figure", with "Show every trip" to drop the filters. Either way the
// period is the shared date range picker, kept in the address (?from, ?to).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaListUl, FaExternalLinkAlt } from 'react-icons/fa';
import { listTrips } from '../../../../services/lexistarTrips';
import { listLanes } from '../../../../services/lexistarRoutes';
import { NoPermissionPage, StagePill as StagePillJs } from '../StatusBoard/atoms';
import { PageHeader, UnifiedDateRangePicker, useAuth } from '../../../../types/commonComponents';
import { SkeletonBlock, cardClasses } from './analyticsAtoms';
import {
  addDays,
  dayString,
  isDay,
  parseDay,
  periodLabel,
  todayString,
  type Period,
} from './periodLogic';
import { formatMiningDateTime } from '../../../../utils/dateUtils';
import type { ComponentType } from 'react';
import PhoneCard, { PhoneCardList } from '../../../common/PhoneCard';
import { usePhoneLayout } from '../../../common/phoneLayout';

const StagePill = StagePillJs as unknown as ComponentType<{ stage: string | null | undefined }>;

const LIMIT = 500;

export interface TripRow {
  id: string;
  reference: string;
  stage: string;
  planned_start: string | null;
  asset_key: string | null;
  registration: string | null;
  driver_id: string | null;
  run_id: string | null;
  load: { consignor?: string | null; material?: string | null } | null;
  source_location_id: number | null;
  destination_location_id: number | null;
  source_name: string | null;
  destination_name: string | null;
  loaded_route_id: string | null;
  legs?: { route_id?: string | null }[];
  status_flags?: string[];
  variance_breach?: boolean | null;
  actual_km?: number | string | null;
  receipt_net_kg?: number | null;
  dispatch_net_kg?: number | null;
  consumed_l?: number | string | null;
  budget_adjusted_l?: number | string | null;
  budget_original_l?: number | string | null;
}

interface Lane {
  id: string;
  from_location_id: number;
  to_location_id: number;
  from_location_name?: string;
  to_location_name?: string;
}

/** Scope filters passed through to GET /lexistar/trips as they are. */
const PASS_THROUGH = ['route_id', 'lane_id', 'driver_id', 'site_id', 'customer', 'material'] as const;
/** The `flag` values GET /lexistar/trips knows (anything else is not sent: the API would refuse it). */
const FLAGS = ['late', 'variance_breach', 'above_route_norm'];

export const SCOPE_FILTERS: { param: string; label: string }[] = [
  { param: 'lane_id', label: 'Lane' },
  { param: 'route_id', label: 'Route' },
  { param: 'asset', label: 'Truck' },
  { param: 'driver_id', label: 'Driver' },
  { param: 'site_id', label: 'Site' },
  { param: 'customer', label: 'Customer' },
  { param: 'material', label: 'Material' },
  { param: 'run_id', label: 'Run' },
];

const FLAG_LABELS: Record<string, string> = {
  late: 'Late',
  variance_breach: 'Mass-variance breach',
  above_route_norm: 'Above the route norm',
};

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The address's period: from / to (SAST days; to defaults to from, from to today), in order. */
export function periodOf(params: URLSearchParams, today: string = todayString()): Period {
  const from = isDay(params.get('from')) ? (params.get('from') as string) : today;
  const to = isDay(params.get('to')) ? (params.get('to') as string) : from;
  return from <= to ? { from, to } : { from: to, to: from };
}

/** A drill-through (a figure's trips) narrows the list; the sidebar's Trip list does not. */
export const isDrillThrough = (params: URLSearchParams): boolean =>
  SCOPE_FILTERS.some((f) => params.get(f.param)) || !!params.get('stage') || !!params.get('flag');

/** The drill URL's filters as GET /lexistar/trips parameters (the window in SAST). */
export function tripsQuery(params: URLSearchParams, period: Period): Record<string, string | number | boolean | undefined> {
  const flag = params.get('flag');
  const q: Record<string, string | number | boolean | undefined> = {
    from: `${period.from}T00:00:00+02:00`,
    to: `${addDays(period.to, 1)}T00:00:00+02:00`,
    stage: params.get('stage') || undefined,
    asset: params.get('asset') || undefined,
    run_id: params.get('run_id') || undefined,
  };
  PASS_THROUGH.forEach((k) => {
    q[k] = params.get(k) || undefined;
  });
  if (flag && FLAGS.includes(flag)) q.flag = flag;
  q.limit = LIMIT;
  return q;
}

export default function TripsDrill() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.trips.view');
  const phone = usePhoneLayout(); // ST-00207
  const [params, setParams] = useSearchParams();
  const period: Period = periodOf(params);
  const drill = isDrillThrough(params);
  // The picker sets the start and then the end of a range: build each on the latest address.
  const latest = useRef(params);
  useEffect(() => {
    latest.current = params;
  }, [params]);
  const setPeriod = useCallback(
    (p: Period) => {
      const next = new URLSearchParams(latest.current);
      next.set('from', p.from);
      next.set('to', p.to);
      latest.current = next;
      setParams(next, { replace: true });
    },
    [setParams]
  );
  const onStart = (d: Date | null) => {
    if (!d) return;
    const from = dayString(d);
    const now = periodOf(latest.current);
    setPeriod({ from, to: now.to < from ? from : now.to });
  };
  const onEnd = (d: Date | null) => {
    if (!d) return;
    const to = dayString(d);
    const now = periodOf(latest.current);
    setPeriod({ from: now.from > to ? to : now.from, to });
  };
  const stage = params.get('stage') || '';
  const needLanes = Boolean(params.get('lane_id'));
  const queryKey = params.toString();

  const [rows, setRows] = useState<TripRow[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    setRows(null);
    setError(null);
    listTrips(tripsQuery(params, period))
      .then((res: { trips?: TripRow[]; pagination?: { total?: number } }) => {
        if (!alive) return;
        setRows(res?.trips || []);
        setTotal(num(res?.pagination?.total));
      })
      .catch((err: { response?: { data?: { error?: string } }; message?: string }) => {
        if (!alive) return;
        setRows([]);
        setError(err.response?.data?.error || err.message || 'The trips could not be loaded.');
      });
    if (needLanes) {
      listLanes()
        .then((res: { lanes?: Lane[] }) => alive && setLanes(res?.lanes || []))
        .catch(() => alive && setLanes([]));
    }
    return () => {
      alive = false;
    };
    // queryKey carries every input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, queryKey]);

  const shown = rows || [];

  const chips = [
    ...SCOPE_FILTERS.filter((f) => params.get(f.param)).map((f) => {
      const v = params.get(f.param) as string;
      const lane = f.param === 'lane_id' ? lanes.find((l) => l.id === v) : undefined;
      const text = lane
        ? `${lane.from_location_name ?? lane.from_location_id} → ${lane.to_location_name ?? lane.to_location_id}`
        : f.param === 'customer' || f.param === 'material'
          ? v
          : v.replace(/^reg:/, '').slice(0, 13);
      return `${f.label}: ${text}`;
    }),
    ...(stage ? [`Stage: ${stage.split(',').map((s) => s.toLowerCase().replace(/_/g, ' ')).join(', ')}`] : []),
    ...(params.get('flag') ? [FLAG_LABELS[params.get('flag') as string] || (params.get('flag') as string)] : []),
  ];

  const header = (
    <PageHeader
      icon={FaListUl}
      title={drill ? 'Trips behind the figure' : 'Trip list'}
      subtitle={
        drill
          ? `${periodLabel(period)} — the trips a KPI, tile or report row counted.`
          : `${periodLabel(period)} — every LexiStar trip planned to start in the period. Open one for its map, weights and timeline.`
      }
    />
  );
  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view trips." />;

  const fmtT = (kg: number | null | undefined) => (kg === null || kg === undefined ? '—' : `${(kg / 1000).toLocaleString('en-GB', { maximumFractionDigits: 1 })} t`);
  const fmtN = (v: unknown, unit: string) => {
    const n = num(v);
    return n === null ? '—' : `${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })} ${unit}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className={phone ? 'pt-4 pb-20 space-y-3' : 'max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-3'}>
        <div className={phone ? 'px-3 flex flex-wrap items-end gap-3' : 'flex flex-wrap items-end gap-3'}>
          <div className="w-full sm:w-64">
            <span className="block text-xs font-medium text-slate-600 mb-1">Period</span>
            <UnifiedDateRangePicker
              startDate={parseDay(period.from)}
              endDate={parseDay(period.to)}
              onStartDateChange={onStart}
              onEndDateChange={onEnd}
              bounds="all"
            />
          </div>
          {drill && (
            <Link
              to={`/lexistar/trips?from=${period.from}&to=${period.to}`}
              className="text-sm font-medium text-primary-700 hover:underline whitespace-nowrap pb-2"
            >
              Show every trip
            </Link>
          )}
        </div>
        <div className={phone ? 'px-3 flex flex-wrap items-center gap-2' : 'flex flex-wrap items-center gap-2'} aria-label="Filters">
          {chips.map((c) => (
            <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface border border-slate-300 text-slate-700">
              {c}
            </span>
          ))}
          <span className="text-xs text-slate-600 tabular-nums" role="status" aria-live="polite">
            {rows === null ? 'Loading trips…' : `${shown.length} trip${shown.length === 1 ? '' : 's'}${total !== null && total > shown.length ? ` (first ${shown.length} of ${total})` : ''}`}
          </span>
        </div>
        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-medium">
            {error}
          </div>
        )}
        <section className={phone && rows?.length ? '' : `${cardClasses} overflow-hidden`} aria-label="Trips">
          {rows === null ? (
            <div className="p-4 space-y-2" aria-busy="true">
              {Array.from({ length: 6 }, (_, i) => (
                <SkeletonBlock key={i} className="h-8" />
              ))}
            </div>
          ) : !shown.length ? (
            <p className="px-4 py-10 text-center text-sm text-slate-600">No trips match.</p>
          ) : phone ? (
            <div>
              <PhoneCardList label="Trips">
                {shown.map((t) => {
                  const truck = t.registration || (t.asset_key || '').replace(/^(reg|asset):/, '') || '—';
                  const budget = t.budget_adjusted_l ?? t.budget_original_l;
                  return (
                    <PhoneCard
                      key={t.id}
                      title={<span className="font-mono">{t.reference}</span>}
                      to={`/lexistar/trips/${t.id}`}
                      openLabel={`Open trip ${t.reference}`}
                      aside={<StagePill stage={t.stage} />}
                    >
                      <div className="text-slate-600 tabular-nums">
                        {t.planned_start ? formatMiningDateTime(t.planned_start).slice(0, 17) : '—'} ·{' '}
                        <span className="font-mono">{truck}</span>
                      </div>
                      <div className="text-slate-800">
                        {t.source_name || '—'} → {t.destination_name || '—'}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-slate-700 tabular-nums">
                        <span>
                          <span className="text-xs text-slate-500">Net </span>
                          {fmtT(t.receipt_net_kg ?? t.dispatch_net_kg)}
                        </span>
                        <span>{fmtN(t.actual_km, 'km')}</span>
                        <span>
                          <span className="text-xs text-slate-500">Fuel </span>
                          {fmtN(t.consumed_l, 'L')}
                          {num(budget) !== null && <span className="text-slate-500"> / {fmtN(budget, 'L')}</span>}
                        </span>
                      </div>
                    </PhoneCard>
                  );
                })}
              </PhoneCardList>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                    <th scope="col" className="px-3 py-2">Trip</th>
                    <th scope="col" className="px-3 py-2">Planned</th>
                    <th scope="col" className="px-3 py-2">Stage</th>
                    <th scope="col" className="px-3 py-2">Truck</th>
                    <th scope="col" className="px-3 py-2">From → to</th>
                    <th scope="col" className="px-3 py-2 text-right">Net mass</th>
                    <th scope="col" className="px-3 py-2 text-right">Km</th>
                    <th scope="col" className="px-3 py-2 text-right">Fuel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {shown.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50 tabular-nums">
                      <th scope="row" className="px-3 py-2 text-left font-normal whitespace-nowrap">
                        <Link to={`/lexistar/trips/${t.id}`} className="inline-flex items-center gap-1 font-medium text-slate-900 hover:underline">
                          {t.reference}
                          <FaExternalLinkAlt size={8} className="text-slate-400" aria-hidden="true" />
                        </Link>
                      </th>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700">{t.planned_start ? formatMiningDateTime(t.planned_start).slice(0, 17) : '—'}</td>
                      <td className="px-3 py-2">
                        <StagePill stage={t.stage} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-800">{t.registration || (t.asset_key || '').replace(/^(reg|asset):/, '') || '—'}</td>
                      <td className="px-3 py-2 text-slate-700 min-w-[12rem]">
                        {t.source_name || '—'} → {t.destination_name || '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-800 whitespace-nowrap">{fmtT(t.receipt_net_kg ?? t.dispatch_net_kg)}</td>
                      <td className="px-3 py-2 text-right text-slate-800 whitespace-nowrap">{fmtN(t.actual_km, 'km')}</td>
                      <td className="px-3 py-2 text-right text-slate-800 whitespace-nowrap">
                        {fmtN(t.consumed_l, 'L')}
                        {num(t.budget_adjusted_l ?? t.budget_original_l) !== null && (
                          <span className="text-slate-500"> / {fmtN(t.budget_adjusted_l ?? t.budget_original_l, 'L')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
